import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Catalog, CatalogInsert, CatalogUpdate } from '@/types/db';

export function useCatalog() {
  const [items, setItems] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadCatalog();
  }, []);

  async function loadCatalog() {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('catalog')
        .select('*')
        .order('name', { ascending: true });

      if (err) throw err;
      // Cast the role_labels from Json to RoleLabels
      setItems((data || []).map(item => ({
        ...item,
        role_labels: (item.role_labels as any) || {},
        enabled_roles: item.enabled_roles || ['applicant'],
      })) as Catalog[]);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }

  async function createCatalogItem(item: CatalogInsert) {
    const { data, error: err } = await supabase
      .from('catalog')
      .insert(item)
      .select()
      .single();

    if (err) throw err;
    await loadCatalog();
    return data;
  }

  async function updateCatalogItem(id: string, updates: CatalogUpdate) {
    const { data, error: err } = await supabase
      .from('catalog')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (err) throw err;
    await loadCatalog();
    return data;
  }

  async function deleteCatalogItem(id: string) {
    const { error: err } = await supabase
      .from('catalog')
      .delete()
      .eq('id', id);

    if (err) throw err;
    await loadCatalog();
  }

  return {
    items,
    loading,
    error,
    loadCatalog,
    createCatalogItem,
    updateCatalogItem,
    deleteCatalogItem,
  };
}
